/**
 * Free-shipping progress bar — convention Smootify native.
 *
 * Structure HTML supportée :
 *
 *   <free-shipping-bar data-quantity="3">  <!-- ou data-amount="50" -->
 *     <div free-shipping-condition="needs-more">
 *       <div>Only <span data-prop="left">3</span> products away from free shipping!</div>
 *     </div>
 *     <div free-shipping-condition="needs-more" class="sm-free-shipping_progress-container">
 *       <div data-prop="progress-width" class="sm-progress-bar"></div>
 *     </div>
 *     <div free-shipping-condition="free-shipping">
 *       Congratulations! You've got free shipping!
 *     </div>
 *   </free-shipping-bar>
 *
 * Comportement :
 *   - data-quantity → seuil par nombre d items (cart.totalQuantity)
 *   - data-amount   → seuil par montant € (cart.cost.subtotalAmount)
 *   - [data-prop="left"] : nombre ou montant restant ; 0 quand atteint
 *   - [data-prop="progress-width"] : style.width = X% (0..100)
 *   - [free-shipping-condition="needs-more"] : visible si !reached
 *   - [free-shipping-condition="free-shipping"] : visible si reached
 *
 * Re-render à chaque smootify:cart_updated.
 */

import { getCurrentCart } from "../cart/state.js";
import type { Cart } from "../types.js";

function pctText(pct: number): string {
  return `${Math.min(100, Math.max(0, pct)).toFixed(2)}%`;
}

function formatEuro(value: number): string {
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)}€`;
  }
}

interface BarState {
  current: number;
  threshold: number;
  remaining: number;
  reached: boolean;
  isMonetary: boolean;
}

function compute(wrapper: HTMLElement, cart: Cart | null): BarState | null {
  const amountAttr = wrapper.getAttribute("data-amount");
  const qtyAttr = wrapper.getAttribute("data-quantity");

  if (amountAttr) {
    const threshold = Number.parseFloat(amountAttr);
    if (!Number.isFinite(threshold) || threshold <= 0) return null;
    const subtotal = Number.parseFloat(
      String(cart?.cost?.subtotalAmount?.amount ?? "0"),
    );
    return {
      current: subtotal,
      threshold,
      remaining: Math.max(0, threshold - subtotal),
      reached: subtotal >= threshold,
      isMonetary: true,
    };
  }
  if (qtyAttr) {
    const threshold = Number.parseInt(qtyAttr, 10);
    if (!Number.isFinite(threshold) || threshold <= 0) return null;
    const totalQty = cart?.totalQuantity ?? 0;
    return {
      current: totalQty,
      threshold,
      remaining: Math.max(0, threshold - totalQty),
      reached: totalQty >= threshold,
      isMonetary: false,
    };
  }
  return null;
}

function hydrate(wrapper: HTMLElement, state: BarState): void {
  const pct = state.threshold > 0 ? (state.current / state.threshold) * 100 : 0;

  // Progress bar fill : [data-prop="progress-width"]
  wrapper.querySelectorAll<HTMLElement>('[data-prop="progress-width"]').forEach((bar) => {
    bar.style.width = pctText(pct);
  });

  // Texte "left" : nombre restant (ou montant si monétaire)
  wrapper.querySelectorAll<HTMLElement>('[data-prop="left"]').forEach((el) => {
    if (state.isMonetary) {
      el.textContent = formatEuro(state.remaining);
    } else {
      el.textContent = String(state.remaining);
    }
  });

  // Conditions Smootify : needs-more (cache si reached), free-shipping (cache sinon)
  wrapper
    .querySelectorAll<HTMLElement>('[free-shipping-condition="needs-more"]')
    .forEach((el) => {
      el.classList.toggle("lmdr-hidden", state.reached);
    });
  wrapper
    .querySelectorAll<HTMLElement>('[free-shipping-condition="free-shipping"]')
    .forEach((el) => {
      el.classList.toggle("lmdr-hidden", !state.reached);
    });

  // Classes wrapper pour styling custom
  wrapper.classList.toggle("lmdr-free-shipping-reached", state.reached);
  wrapper.classList.toggle("lmdr-free-shipping-pending", !state.reached);
}

export function refreshFreeShipping(doc: Document = document): void {
  // Cible : <free-shipping-bar> (Smootify native) OU [data-amount]/[data-quantity]
  // posés sur n'importe quel wrapper.
  const wrappers = doc.querySelectorAll<HTMLElement>(
    "free-shipping-bar, [data-amount], [data-quantity]",
  );
  if (wrappers.length === 0) return;

  const cart = getCurrentCart();

  wrappers.forEach((wrapper) => {
    // Skip si pas une free-shipping bar (filtre faux positifs sur [data-amount/quantity]
    // qui ne contiennent pas de [data-prop="left"] ni [data-prop="progress-width"])
    const isSmootifyTag = wrapper.tagName === "FREE-SHIPPING-BAR";
    const hasProgressMarker =
      wrapper.querySelector(
        '[data-prop="left"], [data-prop="progress-width"], [free-shipping-condition]',
      ) !== null;
    if (!isSmootifyTag && !hasProgressMarker) return;

    const state = compute(wrapper, cart);
    if (state) hydrate(wrapper, state);
  });
}

export function attachFreeShipping(doc: Document = document): void {
  refreshFreeShipping(doc);

  if (typeof document === "undefined") return;
  if ((document as unknown as { __lmdrFreeShippingBound?: boolean }).__lmdrFreeShippingBound) return;
  (document as unknown as { __lmdrFreeShippingBound?: boolean }).__lmdrFreeShippingBound = true;
  document.addEventListener("smootify:cart_updated", () => refreshFreeShipping(doc));
}
